import { useCallback, useEffect, useRef, useState } from "react";
import { projectsApi } from "@/services/projectsApi";
import type { ExtractionColumnDetail, ReportJobStatus } from "@/types/models";

export interface GenerateSectionParams {
  projectId: string;
  templateId: string;
  promptLibraryId: string;
  priming: string;
  documentIds: string[];
  extractionColumnDetails: ExtractionColumnDetail[];
  reportingColumnName: string;
  model: string;
}

export interface SectionStatus {
  status: ReportJobStatus;
  error?: string;
  queuePosition?: number;
  queueSize?: number;
}

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 120; // 10 minutes

export function useReportGeneration() {
  const [sectionStatuses, setSectionStatuses] = useState<Record<string, SectionStatus>>({});

  // One interval ref per section being polled — keyed by promptLibraryId
  const intervalRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const pollCountRefs = useRef<Record<string, number>>({});

  // Clean up all intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(intervalRefs.current).forEach(clearInterval);
    };
  }, []);

  const stopPolling = useCallback((sectionId: string) => {
    if (intervalRefs.current[sectionId] !== undefined) {
      clearInterval(intervalRefs.current[sectionId]);
      delete intervalRefs.current[sectionId];
    }
    delete pollCountRefs.current[sectionId];
  }, []);

  const pollOnce = useCallback(
    async (
      sectionId: string,
      params: { projectId: string; templateId: string },
      onComplete: (text: string) => void,
      onError: (msg: string) => void
    ) => {
      try {
        const data = await projectsApi.getReportStatus(params.projectId, {
          template_id: params.templateId,
          prompt_library_id: sectionId,
        });

        setSectionStatuses((prev) => ({
          ...prev,
          [sectionId]: {
            status: data.status as ReportJobStatus,
            error: data.error,
            queuePosition: data.queue_position,
            queueSize: data.queue_size,
          },
        }));

        if (data.status === "Processed") {
          stopPolling(sectionId);
          if (data.data) {
            onComplete(data.data);
          }
        } else if (data.status === "Error") {
          stopPolling(sectionId);
          onError(data.error ?? "Report generation failed.");
        }
      } catch (err) {
        console.error("Report status poll error:", err);
      }
    },
    [stopPolling]
  );

  const startPolling = useCallback(
    (
      sectionId: string,
      params: { projectId: string; templateId: string },
      onComplete: (text: string) => void,
      onError: (msg: string) => void
    ) => {
      // Clear any existing interval for this section
      stopPolling(sectionId);
      pollCountRefs.current[sectionId] = 0;

      // Immediate first poll
      pollOnce(sectionId, params, onComplete, onError);

      intervalRefs.current[sectionId] = setInterval(async () => {
        pollCountRefs.current[sectionId] = (pollCountRefs.current[sectionId] ?? 0) + 1;
        if (pollCountRefs.current[sectionId] >= MAX_POLLS) {
          stopPolling(sectionId);
          setSectionStatuses((prev) => ({
            ...prev,
            [sectionId]: { status: "Error", error: "Timed out waiting for report generation." },
          }));
          onError("Timed out waiting for report generation.");
          return;
        }
        await pollOnce(sectionId, params, onComplete, onError);
      }, POLL_INTERVAL_MS);
    },
    [pollOnce, stopPolling]
  );

  const generateSection = useCallback(
    async (
      params: GenerateSectionParams,
      onComplete: (text: string) => void,
      onError: (msg: string) => void
    ) => {
      const { projectId, templateId, promptLibraryId } = params;

      setSectionStatuses((prev) => ({
        ...prev,
        [promptLibraryId]: { status: "Queued" },
      }));

      try {
        await projectsApi.enqueueGenerateReport(projectId, {
          project_id: projectId,
          template_id: templateId,
          prompt_library_id: promptLibraryId,
          priming: params.priming,
          document_ids: params.documentIds,
          extraction_column_details: params.extractionColumnDetails,
          reporting_column_name: params.reportingColumnName,
          model: params.model,
        });

        setSectionStatuses((prev) => ({
          ...prev,
          [promptLibraryId]: { status: "Processing" },
        }));

        startPolling(promptLibraryId, { projectId, templateId }, onComplete, onError);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to enqueue report generation.";
        setSectionStatuses((prev) => ({
          ...prev,
          [promptLibraryId]: { status: "Error", error: msg },
        }));
        onError(msg);
      }
    },
    [startPolling]
  );

  /**
   * On page load: check existing status for each section.
   * Restores generated content for "Processed" sections and resumes polling
   * for any still in "Queued" or "Processing" state.
   */
  const initializeSections = useCallback(
    async (
      projectId: string,
      templateId: string,
      promptLibraryIds: string[],
      onContent: (sectionId: string, text: string) => void,
      onError: (sectionId: string, msg: string) => void
    ) => {
      await Promise.all(
        promptLibraryIds.map(async (promptLibraryId) => {
          try {
            const data = await projectsApi.getReportStatus(projectId, {
              template_id: templateId,
              prompt_library_id: promptLibraryId,
            });

            if (data.status === "NotStarted") return;

            setSectionStatuses((prev) => ({
              ...prev,
              [promptLibraryId]: {
                status: data.status as ReportJobStatus,
                error: data.error,
                queuePosition: data.queue_position,
                queueSize: data.queue_size,
              },
            }));

            if (data.status === "Processed" && data.data) {
              onContent(promptLibraryId, data.data);
            } else if (data.status === "Queued" || data.status === "Processing") {
              startPolling(
                promptLibraryId,
                { projectId, templateId },
                (text) => onContent(promptLibraryId, text),
                (msg) => onError(promptLibraryId, msg)
              );
            }
          } catch (err) {
            console.error("Failed to initialize section status:", promptLibraryId, err);
          }
        })
      );
    },
    [startPolling]
  );

  return { sectionStatuses, generateSection, initializeSections, stopPolling };
}
